import React from 'react';
import {Box} from '@mui/material';
import {Link} from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {CARTE, NAVY, TEXTE, TEXTE_SECONDAIRE, VERDICT_COULEURS} from '../../tokens.js';
import FicheMiniCarte from './FicheMiniCarte.js';
import {FLUX_LIBELLES, GROUPE_LIBELLES, STATUT_LIBELLES, formatDate} from './fiche.selectors.js';

const carteSx = {bgcolor: CARTE.fond, border: `1px solid ${CARTE.bordure}`, borderRadius: `${CARTE.radius}px`, boxShadow: CARTE.ombre};

function Champ({libelle, valeur, discret}) {
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
            <Box component="dt" sx={{fontSize: 12, color: TEXTE_SECONDAIRE}}>{libelle}</Box>
            <Box component="dd" sx={{m: 0, fontWeight: 600, color: discret ? TEXTE_SECONDAIRE : TEXTE}}>{valeur}</Box>
        </Box>
    );
}

function PastilleEtat({Icone, children, pointille, couleurs}) {
    return (
        <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: '6px', px: '12px', py: '5px', borderRadius: '999px', fontSize: 12, fontWeight: 700, letterSpacing: '.04em',
            border: pointille ? '1px dashed #B5BCC6' : `1px solid ${couleurs.bordure}`, bgcolor: pointille ? '#FFFFFF' : couleurs.fond, color: pointille ? TEXTE_SECONDAIRE : couleurs.texte}}>
            <Icone sx={{fontSize: 15}}/>{children}
        </Box>
    );
}

/**
 * Fiche d'un PAV sans aucun passage ERP (R7, `FichePAV-Vide.dc.html`) : en-tête patrimoine à l'état NON INSPECTÉ pointillé,
 * encart unique « Aucun passage enregistré sur ce point », prochain passage planifié, lien vers les PAV voisins.
 */
export default function FicheEtatVide({pav, patrimoine, prochain, carteTo, voisinsTo}) {
    const actif = pav.statut === 'ACTIF';
    const point = pav.lat != null && pav.lng != null;
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
            <Box component="section" aria-labelledby="pav-titre" sx={{...carteSx, display: 'flex', gap: '32px', px: '28px', py: '24px', flexWrap: 'wrap'}}>
                <Box sx={{flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px'}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap'}}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                            <Box component="span" sx={{fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: TEXTE_SECONDAIRE}}>Point d'apport volontaire</Box>
                            <Box component="h1" id="pav-titre" sx={{m: 0, fontSize: 34, lineHeight: 1.1, fontWeight: 700, letterSpacing: '-.01em'}}>{pav.nomenclature}</Box>
                            <Box component="span" sx={{fontSize: 15, color: TEXTE}}>
                                {pav.adresse && pav.adresse.trim() ? pav.adresse.trim() : '—'} · {pav.communeLibelle || '—'}
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', gap: '10px', flexWrap: 'wrap', pt: '26px'}}>
                            <PastilleEtat Icone={CheckCircleOutlineIcon} pointille={!actif} couleurs={VERDICT_COULEURS.ras}>
                                {`STATUT · ${(STATUT_LIBELLES[pav.statut] || pav.statut || '—').toUpperCase()}`}
                            </PastilleEtat>
                            <PastilleEtat Icone={HelpOutlineIcon} pointille>ÉTAT · NON INSPECTÉ</PastilleEtat>
                        </Box>
                    </Box>
                    <Box component="dl" sx={{m: 0, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px 24px', pt: '16px', borderTop: `1px solid ${CARTE.separateur}`}}>
                        <Champ libelle="Patrimoine" valeur={patrimoine ? (GROUPE_LIBELLES[patrimoine.groupe] || patrimoine.libelle) : '—'}/>
                        <Champ libelle="Abri-bacs" valeur={pav.quantite != null ? pav.quantite : '—'}/>
                        <Champ libelle="Flux" valeur={FLUX_LIBELLES[pav.flux] || pav.flux || '—'}/>
                        <Champ libelle="Photos disponibles" valeur="0" discret/>
                        <Champ libelle="Dernier lavage" valeur="aucun" discret/>
                        <Champ libelle="Dernière inspection" valeur="aucune" discret/>
                        <Champ libelle="Prochain passage planifié" valeur={formatDate(prochain)}/>
                        <Champ libelle="Eau par lavage" valeur="≈ — L" discret/>
                    </Box>
                </Box>
                <Box component="figure" sx={{m: 0, flex: '0 0 320px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <Box sx={{border: `1px solid ${CARTE.bordure}`, borderRadius: '12px', overflow: 'hidden'}}>
                        <FicheMiniCarte lat={pav.lat} lng={pav.lng} couleur={VERDICT_COULEURS.ni.pastille} hauteur={180} etiquette={`Position du PAV ${pav.nomenclature}`}/>
                    </Box>
                    <Box component="figcaption" sx={{display: 'flex', justifyContent: 'space-between', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                        <span>{point ? `Lat ${pav.lat.toFixed(5)} · Lng ${pav.lng.toFixed(5)}` : 'Lat — · Lng —'}</span>
                        <Box component={Link} to={carteTo} sx={{color: NAVY}}>Ouvrir dans la carte →</Box>
                    </Box>
                </Box>
            </Box>

            <Box component="section" aria-labelledby="vide-titre" sx={{...carteSx, px: '32px', py: '48px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px'}}>
                <EventBusyOutlinedIcon sx={{fontSize: 44, color: '#8A929C'}}/>
                <Box component="h2" id="vide-titre" sx={{m: 0, fontSize: 18, fontWeight: 800, color: NAVY}}>Aucun passage enregistré sur ce point</Box>
                <Box component="p" sx={{m: 0, maxWidth: 520, color: TEXTE_SECONDAIRE}}>
                    Ce PAV fait partie du patrimoine mais aucune intervention Les Eugène (lavage, maintenance) n'a encore été remontée.
                    Son état restera <Box component="strong" sx={{fontWeight: 600, color: TEXTE}}>NON INSPECTÉ</Box> jusqu'au premier passage.
                </Box>
                <Box sx={{display: 'flex', gap: '12px', mt: '8px', flexWrap: 'wrap', justifyContent: 'center'}}>
                    <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: '8px', px: '14px', py: '8px', border: '1px dashed #B5BCC6', borderRadius: '999px', fontSize: 13, color: TEXTE_SECONDAIRE}}>
                        <ScheduleIcon sx={{fontSize: 15}}/>
                        Prochain passage planifié : <Box component="strong" sx={{fontWeight: 600}}>{formatDate(prochain)}</Box>
                    </Box>
                    <Box component={Link} to={voisinsTo} sx={{display: 'inline-flex', alignItems: 'center', height: 36, px: '14px', border: '1px solid #D5DAE1', borderRadius: '999px', bgcolor: '#FFFFFF', fontSize: 13, fontWeight: 600, textDecoration: 'none', color: TEXTE}}>
                        Voir les PAV voisins sur la carte
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
